// Bespoke voxel prefabs (casters, beasts and wagon). Split out of creatures_extra.ts, whose
// CREATURE_PREFABS_EXTRA registry still assembles them.

import { SKIN, box } from './creatures_extra_shared.js';
import type { CharacterPrefab } from './types.js';

export const archmage: CharacterPrefab = {
  name: 'Archmage',
  description: 'Elder wizard with pointed hat and crystal staff',
  maxHp: 22,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    {
      name: 'beard_long',
      voxels: [...box(-1, 9, 2, 3, 2, 1, '#e8e8e8'), ...box(-1, 7, 2, 3, 2, 1, '#e8e8e8')],
    },
    {
      name: 'pointed_hat',
      voxels: [
        ...box(-2, 14, -2, 5, 1, 5, '#3a1a5a'),
        ...box(-1, 15, -1, 3, 1, 3, '#3a1a5a'),
        ...box(0, 16, 0, 1, 1, 1, '#3a1a5a'),
        ...box(0, 17, 0, 1, 1, 1, '#3a1a5a'),
      ],
    },
    {
      name: 'robe',
      voxels: [...box(-2, 6, -1, 5, 5, 3, '#4a2a7a'), ...box(-3, 0, -1, 7, 6, 3, '#4a2a7a')],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, '#4a2a7a') },
    {
      name: 'right_arm_staff',
      voxels: [
        ...box(3, 6, 0, 1, 3, 1, '#4a2a7a'),
        ...box(3, 0, 0, 1, 10, 1, '#5a3a1a'),
        ...box(2, 10, -1, 3, 2, 3, '#88ddff'),
        ...box(2, 12, 0, 3, 1, 1, '#88ddff'),
      ],
    },
  ],
};

export const treant: CharacterPrefab = {
  name: 'Treant',
  description: 'Walking tree with canopy of leaves',
  maxHp: 50,
  skeletonType: 'quadruped',
  parts: [
    { name: 'trunk_lower', voxels: box(-2, 0, -2, 5, 8, 5, '#5a3a1a') },
    { name: 'trunk_upper', voxels: box(-1, 8, -1, 3, 4, 3, '#6b4423') },
    {
      name: 'face',
      voxels: [
        { x: -1, y: 9, z: 2, color: '#ffd700' },
        { x: 1, y: 9, z: 2, color: '#ffd700' },
        { x: 0, y: 8, z: 2, color: '#3a1a0a' },
      ],
    },
    {
      name: 'canopy',
      voxels: [
        ...box(-3, 12, -3, 7, 4, 7, '#2a6a2a'),
        ...box(-4, 13, -2, 1, 2, 5, '#3a7a3a'),
        ...box(4, 13, -2, 1, 2, 5, '#3a7a3a'),
        ...box(-2, 13, -4, 5, 2, 1, '#3a7a3a'),
        ...box(-2, 13, 4, 5, 2, 1, '#3a7a3a'),
      ],
    },
    {
      name: 'arms_branches',
      voxels: [
        ...box(-4, 6, 0, 2, 4, 1, '#5a3a1a'),
        ...box(3, 6, 0, 2, 4, 1, '#5a3a1a'),
        ...box(-4, 10, 0, 2, 2, 2, '#3a7a3a'),
        ...box(3, 10, 0, 2, 2, 2, '#3a7a3a'),
      ],
    },
    {
      name: 'root_feet',
      voxels: [...box(-3, 0, 0, 2, 2, 2, '#3a2010'), ...box(1, 0, 0, 2, 2, 2, '#3a2010')],
    },
  ],
};

export const chaosLord: CharacterPrefab = {
  name: 'Chaos Lord',
  description: 'Dark knight in spiked plate with horned helm',
  maxHp: 50,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, '#3a3a3a') },
    {
      name: 'horned_helm',
      voxels: [
        ...box(-2, 13, -2, 5, 2, 5, '#1a1a1a'),
        ...box(-1, 15, -1, 3, 1, 3, '#1a1a1a'),
        { x: -2, y: 15, z: 0, color: '#1a1a1a' },
        { x: -2, y: 16, z: 0, color: '#1a1a1a' },
        { x: -2, y: 17, z: 0, color: '#8b1a1a' },
        { x: 2, y: 15, z: 0, color: '#1a1a1a' },
        { x: 2, y: 16, z: 0, color: '#1a1a1a' },
        { x: 2, y: 17, z: 0, color: '#8b1a1a' },
      ],
    },
    {
      name: 'torso_plate',
      voxels: [
        ...box(-2, 6, -1, 5, 5, 3, '#2a2a2a'),
        ...box(-2, 6, 1, 5, 1, 1, '#8b1a1a'),
        ...box(-2, 8, 1, 5, 1, 1, '#8b1a1a'),
        ...box(-2, 10, 1, 5, 1, 1, '#8b1a1a'),
      ],
    },
    {
      name: 'shoulder_spikes',
      voxels: [
        { x: -3, y: 11, z: 0, color: '#1a1a1a' },
        { x: -3, y: 12, z: 0, color: '#aaaaaa' },
        { x: 3, y: 11, z: 0, color: '#1a1a1a' },
        { x: 3, y: 12, z: 0, color: '#aaaaaa' },
      ],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, '#2a2a2a') },
    {
      name: 'right_arm_axe',
      voxels: [
        ...box(3, 6, 0, 1, 4, 1, '#2a2a2a'),
        ...box(3, 0, 0, 1, 10, 1, '#3a2a1a'),
        ...box(2, 9, -1, 3, 3, 1, '#aaaaaa'),
        ...box(2, 10, -2, 3, 1, 1, '#aaaaaa'),
      ],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#1a1a1a'), ...box(1, 0, 0, 2, 6, 1, '#1a1a1a')],
    },
  ],
};

// ─── Covered wagon (supply_wagon / convoy) ──────────────────
// Replaces the placeholder goblin mapping for logistics units. Reads as
// a billboarded side-on wagon — wheels at the corners, canvas cover
// dominating the silhouette, draft tongue pointing forward.
export const wagon: CharacterPrefab = {
  name: 'Wagon',
  description: 'Covered supply wagon with canvas arch',
  maxHp: 25,
  parts: [
    {
      name: 'wheels',
      voxels: [
        ...box(-2, 0, -2, 1, 2, 1, '#3a2a1a'),
        ...box(2, 0, -2, 1, 2, 1, '#3a2a1a'),
        ...box(-2, 0, 2, 1, 2, 1, '#3a2a1a'),
        ...box(2, 0, 2, 1, 2, 1, '#3a2a1a'),
        // Iron hubs
        { x: -2, y: 1, z: -2, color: '#888888' },
        { x: 2, y: 1, z: -2, color: '#888888' },
        { x: -2, y: 1, z: 2, color: '#888888' },
        { x: 2, y: 1, z: 2, color: '#888888' },
      ],
    },
    { name: 'bed', voxels: box(-2, 2, -2, 5, 2, 5, '#7a4a1a') },
    {
      name: 'cover',
      voxels: [...box(-2, 4, -2, 5, 2, 5, '#e8dcc8'), ...box(-1, 6, -1, 3, 1, 3, '#e8dcc8')],
    },
    { name: 'tongue', voxels: box(0, 2, 3, 1, 1, 2, '#5a3a1a') },
    {
      name: 'cargo',
      voxels: [
        { x: -1, y: 4, z: -2, color: '#a8742a' },
        { x: 0, y: 4, z: -2, color: '#a8742a' },
        { x: 1, y: 4, z: -2, color: '#a8742a' },
      ],
    },
  ],
};

// ─── Templar (templar / crusader) ───────────────────────────
// Replaces the placeholder death_knight mapping for holy knights.
// White surcoat with red cross, silver helm with red plume, shield and
// longsword. Visually distinct from the undead death_knight despite a
// similar silhouette.
export const templar: CharacterPrefab = {
  name: 'Templar',
  description: 'Holy knight in white surcoat and red cross',
  maxHp: 60,
  parts: [
    {
      name: 'helm',
      voxels: [
        // Polished steel helm
        ...box(-1, 11, -1, 3, 4, 3, '#a8a8a8', { metalness: 1.0, roughness: 0.3 }),
        // Visor slit
        { x: -1, y: 12, z: 2, color: '#1a1a1a' },
        { x: 0, y: 12, z: 2, color: '#1a1a1a' },
        { x: 1, y: 12, z: 2, color: '#1a1a1a' },
        // Red plume on top
        { x: 0, y: 15, z: 0, color: '#cc0000' },
      ],
    },
    {
      name: 'surcoat',
      voxels: [
        ...box(-2, 6, -1, 5, 5, 3, '#e8e8e8'),
        // Red cross — vertical
        { x: 0, y: 7, z: 1, color: '#cc0000' },
        { x: 0, y: 8, z: 1, color: '#cc0000' },
        { x: 0, y: 9, z: 1, color: '#cc0000' },
        // Red cross — horizontal
        { x: -1, y: 8, z: 1, color: '#cc0000' },
        { x: 1, y: 8, z: 1, color: '#cc0000' },
      ],
    },
    {
      name: 'shield',
      voxels: [
        // Painted wood shield (matte)
        ...box(-3, 7, 0, 1, 4, 3, '#e8e8e8', { roughness: 0.8 }),
        // Iron shield boss (metallic)
        { x: -3, y: 8, z: 1, color: '#cc0000' },
        { x: -3, y: 9, z: 1, color: '#cc0000' },
      ],
    },
    {
      name: 'sword_arm',
      voxels: [
        // Iron gauntlet
        ...box(3, 7, 0, 1, 3, 1, '#a8a8a8', { metalness: 1.0, roughness: 0.35 }),
        // Polished steel blade
        ...box(3, 10, 0, 1, 6, 1, '#d0d0d0', { metalness: 1.0, roughness: 0.2 }),
        // Brass guard
        ...box(2, 10, 0, 3, 1, 1, '#aa8830', { metalness: 1.0, roughness: 0.4 }),
        // Brass pommel
        { x: 3, y: 9, z: 0, color: '#aa8830', metalness: 1.0, roughness: 0.4 },
      ],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#888888'), ...box(1, 0, 0, 2, 6, 1, '#888888')],
    },
  ],
};

// ─── Mage (mage) ────────────────────────────────────────────
// Replaces the placeholder sorcerer mapping for spell-casters. Pointed
// hat, long white beard, deep-blue robe with golden rune, crystal-tipped
// staff. Reads as a wise old wizard rather than a demon-flavored sorcerer.
export const mage: CharacterPrefab = {
  name: 'Mage',
  description: 'Robed spell-caster with pointed hat and crystal staff',
  maxHp: 35,
  parts: [
    {
      name: 'hat',
      voxels: [
        ...box(-2, 14, -2, 5, 1, 5, '#1a2a6a'),
        ...box(-1, 15, -1, 3, 1, 3, '#1a2a6a'),
        { x: 0, y: 16, z: 0, color: '#2a3a7a' },
        // Glowing gold star on hat front
        { x: 0, y: 14, z: 2, color: '#ffd700', emissive: '#ffaa00' },
      ],
    },
    {
      name: 'head',
      voxels: box(-1, 11, -1, 3, 3, 3, SKIN),
    },
    {
      name: 'beard',
      voxels: [
        { x: -1, y: 10, z: 2, color: '#e8e8e8' },
        { x: 0, y: 10, z: 2, color: '#e8e8e8' },
        { x: 1, y: 10, z: 2, color: '#e8e8e8' },
        { x: -1, y: 9, z: 2, color: '#e8e8e8' },
        { x: 0, y: 9, z: 2, color: '#e8e8e8' },
        { x: 1, y: 9, z: 2, color: '#e8e8e8' },
        { x: 0, y: 8, z: 2, color: '#e8e8e8' },
      ],
    },
    {
      name: 'eyes',
      voxels: [
        { x: -1, y: 12, z: 2, color: '#44ddff', emissive: '#22aaff' },
        { x: 1, y: 12, z: 2, color: '#44ddff', emissive: '#22aaff' },
      ],
    },
    {
      name: 'robe',
      voxels: [
        ...box(-2, 0, -2, 5, 8, 5, '#1a2a5a'),
        // Trim at collar
        ...box(-2, 7, -1, 5, 1, 3, '#3a4a8a'),
        // Trim at hem
        ...box(-2, 0, -2, 5, 1, 5, '#3a4a8a'),
        // Glowing golden rune on chest
        { x: 0, y: 4, z: 2, color: '#ffd700', emissive: '#aa7700' },
      ],
    },
    {
      name: 'staff',
      voxels: [
        // Shaft
        ...box(3, 0, 0, 1, 14, 1, '#5a3a1a'),
        // Crystal orb on top — bright cyan glow
        { x: 3, y: 15, z: 0, color: '#44ddff', emissive: '#22aaff' },
      ],
    },
    {
      name: 'left_arm',
      voxels: [...box(-3, 6, 0, 1, 4, 1, '#1a2a5a'), { x: -3, y: 10, z: 0, color: SKIN }],
    },
  ],
};
